import { useCallback, useState } from "react";

import {
  AnalyticsFieldName,
  SMTAnalyticsSource,
  SMTEntryAnalyticsEvent,
} from "@/constants/analytics/Analytics";
import { useAnalytics } from "@/services/Analytics";
import ImageViewerModal from "@/ui/composed/media/ImageViewerModal";
import { FAIconName, IconButton, Image, Spinner, Text } from "@/ui/core";
import { Box, HStack } from "@/ui/layout";
import { ColorToken } from "@/ui/theme/tokens";
import { isNotNilNotEmpty } from "@/utils/shared.utils";
import { joinTestIDs } from "@/utils/string.util";

interface SMTEntryImagePreviewProps {
  isUploading: boolean;
  onOverrideOpenImageFullScreen?: () => void;
  photoContentType?: string;
  photoUrl?: string;
  previewImageUrl?: string;
  smtEntryId: string;
  testID: string;
}

const SMTEntryImagePreview = ({
  isUploading,
  onOverrideOpenImageFullScreen,
  photoContentType,
  photoUrl,
  previewImageUrl,
  smtEntryId,
  testID,
}: SMTEntryImagePreviewProps) => {
  const { track } = useAnalytics();
  const [isImageViewerOpen, setIsImageViewerOpen] = useState<boolean>(false);

  const handleOpenImageFullScreen = useCallback(() => {
    track(SMTEntryAnalyticsEvent.ViewFullImagePressed, {
      [AnalyticsFieldName.Component]: SMTEntryImagePreview.name,
      [AnalyticsFieldName.Source]: SMTAnalyticsSource.SMTEntry,
      SMTEntryId: smtEntryId,
    });
    onOverrideOpenImageFullScreen?.();
    setIsImageViewerOpen(true);
  }, [onOverrideOpenImageFullScreen, smtEntryId, track]);

  const handleCloseImageFullScreen = useCallback(() => {
    setIsImageViewerOpen(false);
  }, []);

  if (!isNotNilNotEmpty(previewImageUrl) && !isUploading) {
    return null;
  }

  return (
    <Box
      borderRadius={12}
      height={140}
      overflow="hidden"
      position="relative"
      width="100%"
    >
      {isNotNilNotEmpty(previewImageUrl) && (
        <>
          <Box opacity={isUploading ? 0.8 : 1}>
            <Image
              borderRadius={12}
              contentFit="cover"
              contentType={photoContentType}
              size={{ height: 140, width: "100%" }}
              testID={testID}
              uriSource={previewImageUrl}
            />
          </Box>
          <Box bottom={12} position="absolute" right={12}>
            <IconButton
              iconName={FAIconName.MagnifyingGlassPlus}
              onPress={handleOpenImageFullScreen}
              testID={joinTestIDs(testID, "open-image-viewer")}
              variant="primarySolid"
            />
          </Box>
          {!onOverrideOpenImageFullScreen && (
            <ImageViewerModal
              contentType={photoContentType}
              imageUrl={photoUrl ?? previewImageUrl}
              isVisible={isImageViewerOpen}
              onClose={handleCloseImageFullScreen}
              testID={testID}
            />
          )}
        </>
      )}
      {isUploading && (
        <Box left={12} position="absolute" top={12}>
          <HStack
            alignItems="center"
            backgroundColor={ColorToken.Primary100}
            borderRadius={8}
            gap={8}
            paddingHorizontal={12}
            paddingVertical={6}
          >
            <Spinner color={ColorToken.NeutralBackgroundPrimary} size="small" />
            <Text
              color={ColorToken.NeutralBackgroundPrimary}
              testID={joinTestIDs(testID, "uploading-badge")}
              variant="caption"
            >
              Uploading…
            </Text>
          </HStack>
        </Box>
      )}
    </Box>
  );
};

export default SMTEntryImagePreview;
export type { SMTEntryImagePreviewProps };
